import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { functions } from '../firebase';
import type { TenantAccessSummary } from '../lib/db/types';
import { defaultAuthenticatedPath, tenantNeedsBillingAttention } from '../lib/tenantRouting';
import { useTenant } from '../providers/TenantProvider';

const MAX_ATTEMPTS = 15;
const POLL_DELAY_MS = 3000;

function pickPrimaryTenant(data: { primaryTenantId?: string | null; tenants?: TenantAccessSummary[] }) {
  const tenants = data.tenants || [];
  return tenants.find((tenant) => tenant.tenantId === data.primaryTenantId) || tenants[0] || null;
}

export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshTenants } = useTenant();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [requiresRetry, setRequiresRetry] = useState(false);
  const sessionId = useMemo(() => searchParams.get('session_id'), [searchParams]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attemptsCount = 0;
    const getMyTenantContext = httpsCallable(functions, 'getMyTenantContext');

    const poll = async () => {
      if (cancelled) return;

      setLoading(true);
      setError(null);

      try {
        const result = await getMyTenantContext();
        const data = result.data as { primaryTenantId?: string | null; tenants?: TenantAccessSummary[] };
        const tenant = pickPrimaryTenant(data);

        if (!tenant) {
          setPendingMessage('We created your account, but your workspace is not visible yet.');
        } else if (!tenantNeedsBillingAttention(tenant)) {
          await refreshTenants();
          navigate(defaultAuthenticatedPath(tenant, tenant.roles), { replace: true });
          return;
        } else if (tenant.billingStatus === 'past_due' || tenant.billingStatus === 'unpaid' || tenant.billingStatus === 'canceled') {
          setRequiresRetry(true);
          setPendingMessage('Stripe returned your session, but the subscription still needs attention.');
          return;
        } else {
          setPendingMessage('We’re confirming your payment. This usually takes a moment.');
        }
      } catch (contextError: any) {
        console.error('Error verifying checkout:', contextError);
        setError(contextError.message || 'Unable to verify your subscription yet');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }

      if (cancelled) return;
      attemptsCount += 1;
      if (attemptsCount < MAX_ATTEMPTS) {
        timer = setTimeout(poll, POLL_DELAY_MS);
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [navigate, refreshTenants]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 6 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4 }}>
          <Stack spacing={2.5} alignItems="flex-start">
            <Typography variant="h4" fontWeight={700}>
              Verifying your subscription…
            </Typography>
            <Typography color="text.secondary">
              We’re waiting for the Stripe webhook to confirm the workspace. Tenant activation only happens on the backend after that signal lands.
            </Typography>
            {sessionId && (
              <Typography variant="caption" color="text.secondary">
                Session: {sessionId}
              </Typography>
            )}
            {loading && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={22} />
                <Typography variant="body2">Checking your tenant status...</Typography>
              </Stack>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {pendingMessage && !error && (
              <Alert severity={requiresRetry ? 'warning' : 'info'}>{pendingMessage}</Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="outlined" onClick={() => window.location.reload()}>
                Refresh status
              </Button>
              {requiresRetry && (
                <Button variant="contained" onClick={() => navigate('/checkout/cancelled')}>
                  Retry payment
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default CheckoutSuccessPage;
